import React, { useState, useEffect } from 'react';
import { useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import classNames from 'classnames';

// Utils
import { FormattedMessage } from '../../util/reactIntl';
import { LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { OFFER, REQUEST } from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

// Shared components
import {
  H2,
  H3,
  H4,
  Page,
  NamedLink,
  OrderPanel,
  LayoutSingleColumn,
  SectionText,
  AvatarLarge,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  handleContactUser,
  handleSubmitInquiry,
  handleNavigateToMakeOfferPage,
  handleNavigateToRequestQuotePage,
  handleSubmit,
  priceForSchemaMaybe,
  getDerivedRenderData,
} from './ListingPage.shared';
import Notifications from './Notifications/Notifications';
import SectionReviews from './SectionReviews';
import SectionMapMaybe from './SectionMapMaybe';
import SectionGallery from './SectionGallery';
import CustomListingFields from './CustomListingFields';
import ListingPageAccessWrapper from './ListingPageAccessWrapper';

import css from './ListingPage.module.css';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;
// Milliseconds to 30th of June 2026
const RELEASE_DATE_TIME = 1782820800000;

const IconShield = props => {
  const { className, rootClassName } = props;
  const classes = classNames(rootClassName || css.shieldIcon, className);

  return (
    <svg
      className={classes}
      width="32"
      height="32"
      viewBox="0 0 800 800"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M452.184,64.414l-13.12-2.624C378.866,49.751,320.505,29.724,265.538,2.251c-6.004-3.001-13.071-3.001-19.075,0
				C191.478,29.733,133.136,49.754,72.938,61.79l-13.121,2.624c-9.972,1.994-17.149,10.75-17.149,20.919v78.549
				c0,91.952,36.531,180.151,101.544,245.165l96.704,96.704c8.331,8.331,21.839,8.331,30.17,0l96.704-96.704
				c65.014-65.014,101.544-153.213,101.544-245.165V85.333C469.333,75.164,462.156,66.409,452.184,64.414z M426.667,163.883
				c0,80.636-32.036,157.983-89.048,214.995L256,460.497l-81.619-81.619c-57.012-57.012-89.048-134.359-89.048-214.995v-61.072
				C144.359,90.665,201.669,71.279,256,45.097c54.318,26.174,111.642,45.564,170.667,57.714V163.883z" />
      <path d="M185.756,198.244c-8.331-8.331-21.839-8.331-30.17,0s-8.331,21.839,0,30.17l64,64c8.87,8.87,23.443,8.209,31.474-1.428
				l106.667-128c7.543-9.051,6.32-22.503-2.731-30.046c-9.051-7.543-22.503-6.32-30.046,2.731l-91.712,110.054L185.756,198.244z" />
    </svg>
  );
};

const IconSupport = props => {
  const { className, rootClassName } = props;
  const classes = classNames(rootClassName || css.supportIcon, className);

  return (
    <svg
      className={classes}
      width="24"
      height="24"
      viewBox="0 0 512 512"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M379.734355,174.506667 C373.121022,106.666667 333.014355,-2.13162821e-14 209.067688,-2.13162821e-14
        C85.1210217,-2.13162821e-14 45.014355,106.666667 38.4010217,174.506667 C15.2012632,183.311569
        -0.101643453,205.585799 0.000508304259,230.4 L0.000508304259,260.266667 C0.000508304259,293.256475
        26.7445463,320 59.734355,320 C92.7241638,320 119.467688,293.256475 119.467688,260.266667
        L119.467688,230.4 C119.360431,206.121456 104.619564,184.304973 82.134355,175.146667
        C86.4010217,135.893333 107.307688,42.6666667 209.067688,42.6666667 C310.827688,42.6666667
        331.521022,135.893333 335.787688,175.146667 C313.347976,184.324806 298.68156,206.155851
        298.667688,230.4 L298.667688,260.266667 C298.760356,283.199651 311.928618,304.070103
        332.587688,314.026667 C323.627688,330.88 300.801022,353.706667 244.694355,360.533333
        C233.478863,343.50282 211.780225,336.789048 192.906491,344.509658 C174.032757,352.230268
        163.260418,372.226826 167.196286,392.235189 C171.132153,412.243552 188.675885,426.666667
        209.067688,426.666667 C225.181549,426.577424 239.870491,417.417465 247.041022,402.986667
        C338.561022,392.533333 367.787688,345.386667 376.961022,317.653333 C401.778455,309.61433
        418.468885,286.351502 418.134355,260.266667 L418.134355,230.4 C418.23702,205.585799 402.934114,183.311569
        379.734355,174.506667 Z M76.8010217,260.266667 C76.8010217,269.692326 69.1600148,277.333333
        59.734355,277.333333 C50.3086953,277.333333 42.6676884,269.692326 42.6676884,260.266667
        L42.6676884,230.4 C42.6676884,224.302667 45.9205765,218.668499 51.2010216,215.619833
        C56.4814667,212.571166 62.9872434,212.571166 68.2676885,215.619833 C73.5481336,218.668499
        76.8010217,224.302667 76.8010217,230.4 L76.8010217,260.266667 Z M341.334355,230.4
        C341.334355,220.97434 348.975362,213.333333 358.401022,213.333333 C367.826681,213.333333
        375.467688,220.97434 375.467688,230.4 L375.467688,260.266667 C375.467688,269.692326
        367.826681,277.333333 358.401022,277.333333 C348.975362,277.333333 341.334355,269.692326
        341.334355,260.266667 L341.334355,230.4 Z"/>
    </svg>
  );
};

const IconCancel = props => {
  const { className, rootClassName } = props;
  const classes = classNames(rootClassName || css.cancelIcon, className);

  return (
    <svg
      className={classes}
      width="24"
      height="24"
      viewBox="0 0 72 72"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M34,13H22a1,1,0,0,1,0-2H34a1,1,0,0,1,0,2Z"/>
      <path d="M61,66H11a5.006,5.006,0,0,1-5-5V16a5.006,5.006,0,0,1,5-5h7a1,1,0,0,1,0,2H11a3,3,0,0,0-3,3V61a3,3,0,0,0,3,3H61a3,3,0,0,0,3-3V16a3,3,0,0,0-3-3H54a1,1,0,0,1,0-2h7a5.006,5.006,0,0,1,5,5V61A5.006,5.006,0,0,1,61,66Z"/>
      <path d="M50 13H38a1 1 0 010-2H50a1 1 0 010 2zM34 13H22a1 1 0 010-2H34a1 1 0 010 2z"/>
      <path d="M51.34,62H11a5.006,5.006,0,0,1-5-5V16a5.006,5.006,0,0,1,5-5h7a1,1,0,0,1,0,2H11a3,3,0,0,0-3,3V57a3,3,0,0,0,3,3H51.34a3.024,3.024,0,0,0,2.123-.877l9.66-9.66A3.024,3.024,0,0,0,64,47.34V16a3,3,0,0,0-3-3H54a1,1,0,0,1,0-2h7a5.006,5.006,0,0,1,5,5V47.34a5.035,5.035,0,0,1-1.463,3.537l-9.66,9.66A5.035,5.035,0,0,1,51.34,62Z" />
      <path d="M50 13H38a1 1 0 010-2H50a1 1 0 010 2zM53 61.64a1 1 0 01-1-1V52.43A4.435 4.435 0 0156.43 48h8.21a1 1 0 010 2H56.43A2.433 2.433 0 0054 52.43v8.21A1 1 0 0153 61.64zM34 13H22a1 1 0 010-2H34a1 1 0 010 2z" />
      <path d="M66,25H6V16a5.006,5.006,0,0,1,5-5h7a1,1,0,0,1,0,2H11a3,3,0,0,0-3,3v7H64V16a3,3,0,0,0-3-3H54a1,1,0,0,1,0-2h7a5.006,5.006,0,0,1,5,5Z" />
      <path d="M50,13H38a1,1,0,0,1,0-2H50a1,1,0,0,1,0,2Z" />
      <path d="M36 18a3 3 0 01-3-3V9a3 3 0 016 0v6A3 3 0 0136 18zM36 8a1 1 0 00-1 1v6a1 1 0 002 0V9A1 1 0 0036 8zM20 18a3 3 0 01-3-3V9a3 3 0 016 0v6A3 3 0 0120 18zM20 8a1 1 0 00-1 1v6a1 1 0 002 0V9A1 1 0 0020 8zM52 18a3 3 0 01-3-3V9a3 3 0 016 0v6A3 3 0 0152 18zM52 8a1 1 0 00-1 1v6a1 1 0 002 0V9A1 1 0 0052 8zM36 57A14 14 0 1150 43 14.015 14.015 0 0136 57zm0-26A12 12 0 1048 43 12.013 12.013 0 0036 31z" />
      <path d="M41,49a1,1,0,0,1-.707-.293l-10-10a1,1,0,0,1,1.414-1.414l10,10A1,1,0,0,1,41,49Z" />
      <path d="M31,49a1,1,0,0,1-.707-1.707l10-10a1,1,0,0,1,1.414,1.414l-10,10A1,1,0,0,1,31,49Z" />
    </svg>
  );
};

const IconTrust = props => {
  const { className, rootClassName } = props;
  const classes = classNames(rootClassName || css.trustIcon, className);

  return (
    <svg
      className={classes}
      width="24"
      height="24"
      viewBox="0 0 256 256"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)">
        <path d="M 19.817 88.005 c -0.879 0 -1.752 -0.275 -2.498 -0.818 c -1.321 -0.959
        -1.97 -2.555 -1.694 -4.165 l 4.315 -25.161 c 0.072 -0.419 -0.067 -0.846 -0.371
        -1.143 L 1.289 38.899 c -1.169 -1.14 -1.582 -2.812 -1.077 -4.365 c 0.505 -1.553
        1.822 -2.663 3.437 -2.898 l 25.263 -3.671 c 0.421 -0.061 0.784 -0.325 0.972
        -0.707 L 41.182 4.367 C 41.905 2.903 43.367 1.994 45 1.994 c 1.633 0 3.096 0.909
        3.818 2.373 L 60.116 27.26 c 0.188 0.382 0.551 0.645 0.972 0.707 l 25.264
        3.671 c 1.616 0.235 2.932 1.345 3.436 2.898 c 0.504 1.553 0.092 3.225 -1.078
        4.365 L 70.43 56.718 c -0.304 0.297 -0.443 0.723 -0.371 1.143 l 4.315 25.161 c 0.275
        1.61 -0.374 3.206 -1.694 4.165 c -1.32 0.961 -3.038 1.085 -4.484 0.325 l -22.596
        -11.88 c -0.376 -0.199 -0.825 -0.198 -1.201 -0.001 L 21.803 87.511 C 21.174
        87.841 20.494 88.005 19.817 88.005 z M 45 72.516 c 0.68 0 1.361 0.163 1.982
        0.489 l 22.596 11.88 c 0.64 0.341 1.165 0.044 1.359 -0.098 c 0.195 -0.142 0.636
        -0.549 0.514 -1.263 l -4.315 -25.16 c -0.237 -1.382 0.22 -2.791 1.225 -3.77 l 18.28
        -17.819 c 0.519 -0.506 0.402 -1.094 0.326 -1.323 c -0.074 -0.229 -0.325 -0.774 -1.041
        -0.878 l -25.264 -3.671 c -1.388 -0.202 -2.586 -1.073 -3.206 -2.33 L 46.158
        5.681 c -0.321 -0.65 -0.917 -0.72 -1.158 -0.72 c -0.241 0 -0.837 0.07 -1.157
        0.719 L 32.545 28.573 c -0.621 1.257 -1.819 2.127 -3.206 2.329 L 4.075 34.573 c -0.717
        0.104 -0.967 0.649 -1.042 0.878 c -0.074 0.23 -0.192 0.818 0.326 1.323 L 21.64
        54.594 c 1.004 0.978 1.462 2.388 1.225 3.77 L 18.55 83.524 c -0.123 0.714 0.318
        1.12 0.513 1.262 c 0.196 0.142 0.718 0.436 1.359 0.099 l 22.597 -11.881 C 43.639
        72.679 44.32 72.516 45 72.516 z" />
      </g>
    </svg>
  );
};

const IconVerified = props => {
  const { className, rootClassName } = props;
  const classes = classNames(rootClassName || css.verifiedIcon, className);

  return (
    <svg
      className={classes}
      width="24"
      height="24"
      viewBox="0 0 122.88 116.87"
      xmlns="http://www.w3.org/2000/svg"
    >
      <polygon
        style={{fill: '#10a64a', fillRule: 'evenodd'}}
        points="61.37 8.24 80.43 0 90.88 17.79 111.15 22.32 109.15 42.85 122.88 58.43
        109.2 73.87 111.15 94.55 91 99 80.43 116.87 61.51 108.62 42.45 116.87 32 99.08
        11.73 94.55 13.73 74.01 0 58.43 13.68 42.99 11.73 22.32 31.88 17.87 42.45 0
        61.37 8.24 61.37 8.24" />
      <path
        style={{ fill: '#fff'}}
        d="M37.92,65c-6.07-6.53,3.25-16.26,10-10.1,2.38,2.17,5.84,5.34,8.24,7.49L74.66,
        39.66C81.1,33,91.27,42.78,84.91,49.48L61.67,77.2a7.13,7.13,0,0,1-9.9.44C47.83,
        73.89,42.05,68.5,37.92,65Z" />
    </svg>
  );
};

const IconLocation = props => {
  const { rootClassName, className } = props;
  const classes = classNames(rootClassName || css.locationIcon, className);
  return (
    <svg
      className={classes}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      fill="none"
      viewBox="0 0 24 24"
    >
      <path d="M12 12.5c1.6569 0 3-1.3431 3-3 0-1.65685-1.3431-3-3-3s-3 1.34315-3 3c0 1.6569 1.3431 3 3 3Z" />
      <path d="M19.5 9.5c0 6.5833-5.7762 11.6103-7.1904 12.4178-.0943.0539-.201.0822-.3096.0822-.1086 0-.2153-.0283-.3096-.0822C10.2763 21.1095 4.5 16.0817 4.5 9.5c0-1.98912.79018-3.89678 2.1967-5.3033S10.0109 2 12 2s3.8968.79018 5.3033 2.1967C18.7098 5.60322 19.5 7.51088 19.5 9.5Z" />
    </svg>
  );
};

const IconDoubleBed = props => {
  const { rootClassName, className } = props;
  const classes = classNames(rootClassName || css.doubleBedIcon, className);
  return (
    <svg
      className={classes}
      xmlns="http://www.w3.org/2000/svg"
      width="32"
      height="32"
      fill="none"
      viewBox="0 0 24 24"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2 10.1707V6C2 4.34315 3.34315 3 5 3H19C20.6569 3 22 4.34315 22 6V10.1707C23.1652
        10.5825 24 11.6938 24 13V20C24 20.5523 23.5523 21 23 21C22.4477 21 22 20.5523 22
        20V19H2V20C2 20.5523 1.55228 21 1 21C0.447715 21 0 20.5523 0 20V13C0 11.6938 0.834808
        10.5825 2 10.1707ZM4 6C4 5.44772 4.44772 5 5 5H19C19.5523 5 20 5.44772 20 6V10H19V9C19
        7.34315 17.6569 6 16 6H14C13.2316 6 12.5308 6.28885 12 6.7639C11.4692 6.28885 10.7684
        6 10 6H8C6.34315 6 5 7.34315 5 9V10H4V6ZM17 10V9C17 8.44772 16.5523 8 16 8H14C13.4477
        8 13 8.44772 13 9V10H17ZM11 10V9C11 8.44772 10.5523 8 10 8H8C7.44772 8 7 8.44772 7
        9V10H11ZM21 12C21.5523 12 22 12.4477 22 13V17H2V13C2 12.4477 2.44772 12 3 12H21Z"
      />
    </svg>
  );
};

const IconWhiteboard = props => {
  const { rootClassName, className } = props;
  const classes = classNames(rootClassName || css.whiteboardIcon, className);
  return (
    <svg
      className={classes}
      xmlns="http://www.w3.org/2000/svg"
      width="32"
      height="32"
      fill="none"
      viewBox="0 0 512 512"
    >
      <path
        d="M224,288l0,32l272,0c8.837,0 16,7.163 16,16c0,8.837 -7.163,16
        -16,16l-160.584,0l36.744,137.133c2.287,8.535 -2.778,17.309 -11.313,19.596c-8.536,2.287
        -17.309,-2.778 -19.596,-11.314l-38.964,-145.415l-30.287,0l0,96c0,8.837 -7.163,16
        -16,16c-8.837,0 -16,-7.163 -16,-16l0,-96l-30.287,0l-38.964,145.415c-2.287,8.536
        -11.06,13.601 -19.596,11.314c-8.535,-2.287 -13.6,-11.061 -11.313,-19.596l36.744,
        -137.133l-160.584,0c-8.884,-0.048 -16,-7.193 -16,-16c0,-8.807 7.116,-15.952 16,
        -16l80,0l0,-32l128,0Zm-160,0l-32,0l0,-256c0,-17.673 14.327,-32 32,-32l384,0c17.673,0
        32,14.327 32,32l0,256l-32,0l0,-256l-384,0l0,256Z"/>
      />
    </svg>
  );
};

const IconCommunity = props => {
  const { rootClassName, className } = props;
  const classes = classNames(rootClassName || css.communityIcon, className);
  return (
    <svg
      className={classes}
      xmlns="http://www.w3.org/2000/svg"
      width="40"
      height="40"
      fill="none"
      viewBox="0 0 256 256"
    >
      <g
        style={{ stroke: 'none', strokeWidth: 0, strokeDasharray: 'none', strokeLinecap: 'butt', strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: 'none', fillRule: 'nonzero', opacity: 1 }}
        transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)">
        <path
          d="M 70.562 48.153 h -9.579 c -6.109 0 -11.08 -4.971 -11.08 -11.08 v -9.255 c 0
          -2.703 -2.199 -4.902 -4.902 -4.902 s -4.902 2.199 -4.902 4.902 v 9.255 c 0 6.109
          -4.97 11.08 -11.08 11.08 h -9.579 C 8.72 48.153 0 56.873 0 67.592 v 8.673 c 0
          2.703 2.199 4.902 4.902 4.902 s 4.902 -2.199 4.902 -4.902 V 69.01 c 0 -2.001
          0.528 -3.922 1.518 -5.604 v 16.024 c 0 1.539 1.252 2.791 2.791 2.791 h 21.676 c
          1.539 0 2.791 -1.252 2.791 -2.791 V 56.157 c 2.499 -1.148 4.666 -2.785 6.423 -4.762 c
          1.788 2.01 3.977 3.638 6.417 4.761 v 23.274 c 0 1.539 1.252 2.791 2.791 2.791 h 21.676 c
          1.539 0 2.791 -1.252 2.791 -2.791 V 63.406 c 0.99 1.681 1.518 3.603 1.518 5.604 v
          8.255 c 0 2.703 2.199 4.902 4.902 4.902 S 90 79.968 90 77.265 v -9.673 C 90 56.873
          81.28 48.153 70.562 48.153 z M 37.194 54.582 c -0.372 0.155 -0.614 0.519 -0.614 0.922 v
          23.927 c 0 0.437 -0.355 0.791 -0.791 0.791 H 14.113 c -0.436 0 -0.791 -0.354 -0.791
          -0.791 V 60.491 c 0 -0.405 -0.244 -0.77 -0.618 -0.925 c -0.124 -0.051 -0.253 -0.075
          -0.382 -0.075 c -0.261 0 -0.518 0.103 -0.709 0.295 c -2.456 2.468 -3.809 5.743 -3.809
          9.224 v 7.255 c 0 1.601 -1.302 2.902 -2.902 2.902 S 2 77.865 2 76.265 v -8.673 c 0
          -9.615 7.823 -17.438 17.439 -17.438 h 9.579 c 6.556 0 11.986 -4.855 12.922 -11.155 c
          0.091 -0.151 0.158 -0.318 0.158 -0.507 v -1.418 v -9.255 c 0 -1.601 1.302 -2.902 2.902
          -2.902 s 2.902 1.302 2.902 2.902 v 9.255 v 1.418 C 47.902 45.541 43.699 51.856 37.194
          54.582 z M 88 77.265 c 0 1.601 -1.302 2.902 -2.902 2.902 s -2.902 -1.302 -2.902 -2.902 V
          69.01 c 0 -3.481 -1.353 -6.757 -3.809 -9.224 c -0.286 -0.288 -0.716 -0.376 -1.091
          -0.219 c -0.374 0.154 -0.618 0.519 -0.618 0.924 v 18.939 c 0 0.437 -0.354 0.791 -0.791
          0.791 H 54.211 c -0.437 0 -0.791 -0.354 -0.791 -0.791 V 55.504 c 0 -0.403 -0.242 -0.767
          -0.613 -0.922 c -2.517 -1.055 -4.758 -2.704 -6.533 -4.783 c 1.444 -2.018 2.496 -4.308
          3.083 -6.758 c 2.174 4.217 6.564 7.112 11.625 7.112 h 9.579 C 80.177 50.153 88 57.977
          88 67.592 V 77.265 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: '#37CDA3',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) "
          strokeLinecap="round"
        />
        <path
          d="M 56.291 35.957 c 0 4.696 3.82 8.517 8.516 8.517 c 4.696 0 8.517 -3.82 8.517
          -8.517 v -5.713 c 0 -4.696 -3.82 -8.517 -8.517 -8.517 c -4.695 0 -8.516 3.82
          -8.516 8.517 V 35.957 z M 58.291 30.244 c 0 -3.593 2.923 -6.517 6.516 -6.517 c
          3.594 0 6.517 2.923 6.517 6.517 v 5.713 c 0 3.593 -2.923 6.517 -6.517 6.517 c
          -3.593 0 -6.516 -2.923 -6.516 -6.517 V 30.244 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: '#37CDA3',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) " strokeLinecap="round" />
        <path
          d="M 25.193 44.473 c 4.696 0 8.516 -3.82 8.516 -8.517 v -5.713 c 0 -4.696 -3.82
          -8.517 -8.516 -8.517 c -4.696 0 -8.517 3.82 -8.517 8.517 v 5.713 C 16.677 40.653
          20.497 44.473 25.193 44.473 z M 18.677 30.244 c 0 -3.593 2.923 -6.517 6.517 -6.517 c
          3.593 0 6.516 2.923 6.516 6.517 v 5.713 c 0 3.593 -2.923 6.517 -6.516 6.517 c
          -3.593 0 -6.517 -2.923 -6.517 -6.517 V 30.244 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: '#37CDA3',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) " strokeLinecap="round" />
        <path
          d="M 45 18.243 c 0.552 0 1 -0.448 1 -1 V 8.778 c 0 -0.552 -0.448 -1 -1 -1 s -1
          0.448 -1 1 v 8.464 C 44 17.795 44.448 18.243 45 18.243 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: '#37CDA3',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) " strokeLinecap="round" />
        <path
          d="M 50.501 21.243 c 0.215 0 0.432 -0.069 0.614 -0.212 l 7 -5.464 c 0.436 -0.34
          0.513 -0.968 0.173 -1.403 c -0.34 -0.436 -0.969 -0.515 -1.403 -0.173 l -7
          5.464 c -0.436 0.34 -0.513 0.968 -0.173 1.403 C 49.909 21.111 50.203 21.243
          50.501 21.243 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: '#37CDA3',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) " strokeLinecap="round" />
        <path
          d="M 38.885 21.031 c 0.183 0.143 0.399 0.212 0.615 0.212 c 0.297 0 0.592 -0.132
          0.789 -0.385 c 0.34 -0.435 0.263 -1.063 -0.173 -1.403 l -7 -5.464 c -0.434
          -0.341 -1.064 -0.263 -1.403 0.173 c -0.34 0.435 -0.263 1.063 0.173 1.403 L
          38.885 21.031 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: '#37CDA3',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) " strokeLinecap="round" />
      </g>
    </svg>
  );
};

const IconShower = props => {
  const { rootClassName, className } = props;
  const classes = classNames(rootClassName || css.showerIcon, className);
  return (
    <svg
      className={classes}
      xmlns="http://www.w3.org/2000/svg"
      width="40"
      height="40"
      fill="none"
      viewBox="0 0 100 100"
    >
      <path
        d="m83.397 45.72h-66.783c0-11.309 9.167-20.469 20.469-20.469h5.729v
        -25.462h14.388v25.462h5.729c11.302 0 20.468 9.161 20.468 20.469z" />
      <path
        d="m53.417 53.884v-0.032c0-1.882-1.523-3.392-3.411-3.392s-3.412 1.543-3.412
        3.424c0 1.889 1.523 3.412 3.412 3.412 1.888 0 3.411-1.524 3.411-3.412z" />
      <path
        d="m53.417 82.243v-0.071c0-1.888-1.523-3.379-3.411-3.379s-3.412 1.562-3.412
        3.45c0 1.882 1.523 3.412 3.412 3.412 1.888 0 3.411-1.53 3.411-3.412z" />
      <path
        d="m53.417 68.07v-0.078c0-1.882-1.523-3.366-3.411-3.366s-3.412 1.557-3.412 3.444c0 1.882 1.523 3.411 3.412 3.411 1.888 0 3.411-1.529 3.411-3.411z"
      />
      <path
        d="m53.417 96.378v-0.033c0-1.888-1.523-3.398-3.411-3.398s-3.412 1.55-3.412
        3.432c0 1.888 1.523 3.411 3.412 3.411 1.888-0.001 3.411-1.524 3.411-3.412z" />
      <path
        d="m90.064 95.714c1.758-0.703 2.598-2.689 1.901-4.434l-0.026-0.065c-0.69-1.751
        -2.669-2.585-4.414-1.862-1.758 0.697-2.585 2.715-1.888 4.46 0.533 1.335 1.81
        2.148 3.164 2.148 0.417 0 0.853-0.085 1.263-0.247z" />
      <path
        d="m85.357 82.836c1.751-0.696 2.598-2.683 1.901-4.427l-0.026-0.072c-0.703-1.751-2.683-2.591-4.421-1.861-1.751 0.696-2.584 2.715-1.881 4.459 0.527 1.335 1.81 2.148 3.164 2.148 0.417 0 0.853-0.078 1.263-0.247z"
      />
      <path
        d="m80.364 70.062c1.803-0.54 2.825-2.441 2.285-4.245l-0.02-0.064c-0.541-1.804
        -2.422-2.793-4.238-2.253-1.797 0.54-2.819 2.468-2.272 4.277 0.437 1.478 1.797
        2.429 3.262 2.429 0.325 0 0.651-0.046 0.983-0.144z" />
      <path
        d="m75.943 57.081c1.751-0.697 2.598-2.683 1.901-4.428l-0.026-0.064c-0.696-1.745
        -2.676-2.585-4.421-1.869-1.751 0.703-2.584 2.721-1.881 4.466 0.533 1.328 1.81
        2.142 3.164 2.142 0.417 0 0.853-0.078 1.263-0.247z" />
      <path
        d="m69.967 97.901c1.855-0.365 3.054-2.162 2.683-4.011l-0.014-0.065c-0.371-1.849
        -2.181-3.027-4.004-2.643-1.855 0.364-3.047 2.2-2.669 4.043 0.325 1.627 1.745
        2.747 3.333 2.747 0.228 0.001 0.456-0.019 0.671-0.071z" />
      <path
        d="m67.532 84.411c1.875-0.188 3.242-1.861 3.053-3.736l-0.006-0.065c-0.183-1.868
        -1.85-3.235-3.725-3.015-1.881 0.183-3.248 1.889-3.053 3.764 0.176 1.758 1.66
        3.072 3.385 3.072 0.112 0 0.235-0.006 0.346-0.02z" />
      <path
        d="m65.755 70.812c1.849-0.365 3.047-2.162 2.676-4.011l-0.014-0.065c-0.364-1.849
        -2.168-3.014-4.004-2.643-1.849 0.364-3.04 2.193-2.669 4.043 0.325 1.627 1.745
        2.74 3.333 2.74 0.229 0 0.456-0.02 0.678-0.064z" />
      <path
        d="m63.646 57.263c1.855-0.364 3.053-2.161 2.682-4.01l-0.013-0.065c-0.371-1.849
        -2.2-3.027-4.004-2.643-1.855 0.364-3.047 2.194-2.669 4.042 0.325 1.621 1.744
        2.741 3.333 2.741 0.227 0 0.456-0.019 0.671-0.065z" />
      <path
        d="m14.375 93.812l0.026-0.065c0.703-1.744-0.156-3.697-1.914-4.395-1.725-0.703
        -3.744 0.176-4.44 1.928-0.697 1.744 0.143 3.73 1.901 4.434 0.41 0.162 0.846
        0.247 1.263 0.247 1.353 0 2.629-0.814 3.164-2.149z" />
      <path
        d="m19.082 80.935l0.026-0.064c0.703-1.745-0.156-3.698-1.908-4.395-1.731-0.703
        -3.743 0.176-4.446 1.934-0.697 1.744 0.149 3.73 1.901 4.427 0.41 0.169 0.846
        0.247 1.263 0.247 1.353-0.001 2.636-0.814 3.164-2.149z" />
      <path
        d="m23.893 67.777l0.02-0.071c0.547-1.804-0.495-3.666-2.292-4.206-1.836-0.573
        -3.724 0.508-4.258 2.317-0.541 1.804 0.481 3.705 2.285 4.245 0.332 0.098 0.657
        0.144 0.983 0.144 1.465 0 2.826-0.951 3.262-2.429z" />
      <path
        d="m28.496 55.187l0.026-0.065c0.703-1.751-0.156-3.698-1.908-4.401-1.719-0.703
        -3.743 0.182-4.446 1.933-0.697 1.745 0.149 3.73 1.901 4.428 0.41 0.169 0.846
        0.247 1.263 0.247 1.354-0.001 2.629-0.814 3.164-2.142z" />
      <path
        d="m34.049 95.226l0.02-0.065c0.371-1.849-0.833-3.613-2.689-3.978-1.836-0.378
        -3.639 0.859-4.017 2.708-0.371 1.849 0.827 3.646 2.682 4.011 0.215 0.052 0.443
        0.071 0.67 0.071 1.589 0 3.008-1.12 3.334-2.747z" />
      <path
        d="m36.21 81.358l0.006-0.065c0.195-1.875-1.178-3.516-3.06-3.698-1.868-0.221
        -3.542 1.211-3.73 3.08-0.188 1.875 1.179 3.548 3.054 3.736 0.11 0.014 0.234
        0.02 0.345 0.02 1.725 0 3.21-1.315 3.385-3.073z" />
      <path
        d="m38.268 68.136l0.013-0.065c0.371-1.849-0.833-3.613-2.683-3.978-1.81-0.404
        -3.646 0.859-4.017 2.708s0.827 3.646 2.676 4.011c0.221 0.045 0.449 0.064 0.677
        0.064 1.589 0 3.008-1.113 3.334-2.74z" />
      <path
        d="m40.371 54.587l0.02-0.071c0.371-1.843-0.833-3.607-2.689-3.971-1.816-0.384
        -3.646 0.866-4.017 2.708-0.371 1.849 0.827 3.646 2.683 4.01 0.215 0.046 0.442
        0.065 0.67 0.065 1.587 0 3.007-1.12 3.333-2.741z" />
    </svg>
  );
};

const IconCalendar = props => {
  const { rootClassName, className } = props;
  const classes = classNames(rootClassName || css.calendarIcon, className);
  return (
    <svg
      className={classes}
      xmlns="http://www.w3.org/2000/svg"
      width="32"
      height="32"
      fill="none"
      viewBox="0 0 256 256"
    >
      <g
        style={{
          stroke: 'none', strokeWidth: 0, strokeDasharray: 'none', strokeLinecap: 'butt',
          strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: 'none',
          fillRule: 'nonzero', opacity: 1
        }}
        transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)"
      >
        <path
          d="M 86.121 27.37 H 3.878 c -0.552 0 -1 -0.448 -1 -1 V 15.586 c 0 -4.915 3.999
          -8.913 8.913 -8.913 h 66.417 c 4.915 0 8.913 3.999 8.913 8.913 V 26.37 C 87.121
          26.922 86.674 27.37 86.121 27.37 z M 4.878 25.37 h 80.243 v -9.784 c 0 -3.812
          -3.102 -6.913 -6.913 -6.913 H 11.792 c -3.812 0 -6.913 3.101 -6.913 6.913 V 25.37 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: '#37CDA3',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) "
          strokeLinecap="round" />
        <path
          d="M 43.087 90 H 11.792 c -4.915 0 -8.913 -3.998 -8.913 -8.913 V 26.37 c 0
          -0.552 0.448 -1 1 -1 h 82.243 c 0.553 0 1 0.448 1 1 v 19.595 c 0 0.552 -0.447
          1 -1 1 s -1 -0.448 -1 -1 V 27.37 H 4.878 v 53.717 c 0 3.812 3.101 6.913
          6.913 6.913 h 31.295 c 0.552 0 1 0.447 1 1 S 43.639 90 43.087 90 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: '#37CDA3',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) " strokeLinecap="round" />
        <path
          d="M 20.294 15.349 c -0.552 0 -1 -0.448 -1 -1 V 1 c 0 -0.552 0.448 -1 1 -1 s
          1 0.448 1 1 v 13.349 C 21.294 14.901 20.846 15.349 20.294 15.349 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: '#37CDA3',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) "
          strokeLinecap="round" />
        <path
          d="M 69.706 15.349 c -0.553 0 -1 -0.448 -1 -1 V 1 c 0 -0.552 0.447 -1 1 -1 s 1
          0.448 1 1 v 13.349 C 70.706 14.901 70.259 15.349 69.706 15.349 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: '#37CDA3',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) "
          strokeLinecap="round" />
        <path
          d="M 45 15.349 c -0.552 0 -1 -0.448 -1 -1 V 1 c 0 -0.552 0.448 -1 1 -1 s 1
          0.448 1 1 v 13.349 C 46 14.901 45.552 15.349 45 15.349 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: '#37CDA3',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) "
          strokeLinecap="round" />
        <path
          d="M 20.686 41.647 h -1.263 c -0.57 0 -1.032 -0.462 -1.032 -1.032 v -1.263 c 0
          -0.57 0.462 -1.032 1.032 -1.032 h 1.263 c 0.57 0 1.032 0.462 1.032 1.032 v
          1.263 C 21.718 41.185 21.256 41.647 20.686 41.647 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: '#37CDA3',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) "
          strokeLinecap="round" />
        <path
          d="M 37.316 41.647 h -1.263 c -0.57 0 -1.032 -0.462 -1.032 -1.032 v -1.263 c 0
          -0.57 0.462 -1.032 1.032 -1.032 h 1.263 c 0.57 0 1.032 0.462 1.032 1.032 v
          1.263 C 38.348 41.185 37.886 41.647 37.316 41.647 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: '#37CDA3',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) "
          strokeLinecap="round" />
        <path
          d="M 53.947 41.647 h -1.263 c -0.57 0 -1.032 -0.462 -1.032 -1.032 v -1.263 c 0
          -0.57 0.462 -1.032 1.032 -1.032 h 1.263 c 0.57 0 1.032 0.462 1.032 1.032 v
          1.263 C 54.978 41.185 54.516 41.647 53.947 41.647 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: '#37CDA3',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) "
          strokeLinecap="round" />
        <path
          d="M 70.577 41.647 h -1.263 c -0.57 0 -1.032 -0.462 -1.032 -1.032 v -1.263 c 0
          -0.57 0.462 -1.032 1.032 -1.032 h 1.263 c 0.57 0 1.032 0.462 1.032 1.032 v
          1.263 C 71.608 41.185 71.146 41.647 70.577 41.647 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: '#37CDA3',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) "
          strokeLinecap="round" />
        <path
          d="M 20.686 58.277 h -1.263 c -0.57 0 -1.032 -0.462 -1.032 -1.032 v -1.263 c 0
          -0.57 0.462 -1.032 1.032 -1.032 h 1.263 c 0.57 0 1.032 0.462 1.032 1.032 v
          1.263 C 21.718 57.816 21.256 58.277 20.686 58.277 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: '#37CDA3',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) "
          strokeLinecap="round" />
        <path
          d="M 37.316 58.277 h -1.263 c -0.57 0 -1.032 -0.462 -1.032 -1.032 v -1.263 c 0
          -0.57 0.462 -1.032 1.032 -1.032 h 1.263 c 0.57 0 1.032 0.462 1.032 1.032 v
          1.263 C 38.348 57.816 37.886 58.277 37.316 58.277 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLlinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: '#37CDA3',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) "
          strokeLinecap="round" />
        <path
          d="M 20.686 74.907 h -1.263 c -0.57 0 -1.032 -0.462 -1.032 -1.032 v -1.263 c 0
          -0.57 0.462 -1.032 1.032 -1.032 h 1.263 c 0.57 0 1.032 0.462 1.032 1.032 v
          1.263 C 21.718 74.446 21.256 74.907 20.686 74.907 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: '#37CDA3',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) "
          strokeLinecap="round" />
        <path
          d="M 37.316 74.907 h -1.263 c -0.57 0 -1.032 -0.462 -1.032 -1.032 v -1.263 c 0
          -0.57 0.462 -1.032 1.032 -1.032 h 1.263 c 0.57 0 1.032 0.462 1.032 1.032 v
          1.263 C 38.348 74.446 37.886 74.907 37.316 74.907 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: '#37CDA3',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) "
          strokeLinecap="round" />
        <path
          d="M 67.276 90 c -10.943 0 -19.846 -8.902 -19.846 -19.846 s 8.902 -19.846
          19.846 -19.846 c 10.942 0 19.845 8.902 19.845 19.846 S 78.219 90 67.276 90 z M
          67.276 52.309 c -9.84 0 -17.846 8.006 -17.846 17.846 S 57.437 88 67.276 88 s
          17.845 -8.006 17.845 -17.846 S 77.116 52.309 67.276 52.309 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: '#37CDA3',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) "
          strokeLinecap="round" />
        <path
          d="M 76.212 71.154 h -8.936 c -0.553 0 -1 -0.447 -1 -1 V 59.686 c 0 -0.553
          0.447 -1 1 -1 s 1 0.447 1 1 v 9.469 h 7.936 c 0.553 0 1 0.447 1 1 S 76.765
          71.154 76.212 71.154 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: '#37CDA3',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) "
          strokeLinecap="round" />
      </g>
    </svg>
  );
};

const IconThreeLeaves = props => {
  const { rootClassName, className } = props;
  const classes = classNames(rootClassName || css.whiteboardIcon, className);
  return (
    <svg
      className={classes}
      xmlns="http://www.w3.org/2000/svg"
      width="40"
      height="40"
      fill="none"
      viewBox="0 0 512 512"
    >
      <path
        fill="#37CDA3"
        d="M161.563 19.28c10.093 10.734 16.743 23.678 20.562 37.657 6.202 22.707 5.806
        48.148 3.72 74.094-4.176 51.894-14.58 106.512-2.44 140.407 11.396 31.814 29.668
        50.71 49.94 60 8.72 3.998 17.9 6.21 27.186 6.782 22.195-65.084 17.46-148.144-19.06-204.283
        48.352 48.234 71.19 121.068 56.436 197.407l-8.78 3.53c2.723-.808 5.42-1.763
        8.093-2.843 41.783-16.87 75.975-66.253 61.75-138.655-6.536-33.265-28.966-80.165
        -66.5-116.5-32.472-31.432-75.827-55.212-130.908-57.594zM445.53 202.813c-.84
        12.1-4.638 23.528-10.56 33.907-10.353 18.14-26.448 33.757-43.876 48.593-34.856
        29.67-75.057 57.156-88.313 85.218-12.555 26.583-12.884 49.252-6.186 67.283
        2.92 7.86 7.21 14.894 12.625 20.968 53.708-26.787 101.73-80.91 113.81-138.03.076
        59.646-30.63 118.687-86.624 156.906 35.802 14.545 86.282 5.034 121.72-47.75 16.418-24.456
        31.558-67.3 30.812-112.875-.634-38.688-12.264-79.23-43.407-114.217zm-424.874
        73.47c-9.483 45.878.708 86.832 19.5 120.656 22.136 39.84 56.682 69.376 83.125
        82.343 57.07 27.988 105.514 10.968 129.25-19.53-67.59-5.1-123.692-40.873-153.436
        -92.563 39.02 43.428 107.658 66.29 167.562 62.625 1.654-7.963 1.87-16.204.47
        -24.468-3.214-18.965-14.87-38.447-39.032-55.188-25.505-17.67-74.045-21.36-119.063
        -29.625-22.508-4.13-44.247-9.59-62.28-20.124-10.323-6.03-19.314-14.06-26.094-24.125z" />
    </svg>
  );
};

const IconWhiteboard2 = props => {
  const { rootClassName, className } = props;
  const classes = classNames(rootClassName || css.whiteboardIcon2, className);
  return (
    <svg
      className={classes}
      xmlns="http://www.w3.org/2000/svg"
      width="40"
      height="40"
      fill="none"
      viewBox="0 0 512 512"
    >
      <g>
        <path d="M512,0H0v40h16v296h480V40h16V0z M464,304H48V40h416V304z" />
        <rect x="240" y="352" className="st0" width="32" height="160" />
        <polygon className="st0" points="113.274,512 145.274,512 212.18,352 180.18,352 	" />
        <polygon className="st0" points="299.821,352 366.726,512 398.726,512 331.821,352 	" />
        <rect x="128" y="172.016" className="st0" width="64" height="104" />
        <rect x="224" y="132.016" className="st0" width="64" height="144" />
        <rect x="320" y="92.016" className="st0" width="64" height="184" />
      </g>
    </svg>
  );
};

const IconCommunity2 = props => {
  const { rootClassName, className } = props;
  const classes = classNames(rootClassName || css.communityIcon2, className);
  return (
    <svg
      className={classes}
      xmlns="http://www.w3.org/2000/svg"
      width="40"
      height="40"
      fill="none"
      viewBox="0 0 256 256"
    >
      <g
        style={{
          stroke: 'none', strokeWidth: 0, strokeDasharray: 'none', strokeLinecap: 'butt',
          strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: 'none', fillRule: 'nonzero',
          opacity: 1
        }}
        transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)">
        <path
          d="M 25.932 66.453 c 2.978 -1.908 4.961 -5.239 4.961 -9.031 v -4.49 c 0 -5.915
          -4.813 -10.728 -10.728 -10.728 c -5.915 0 -10.728 4.813 -10.728 10.728 v 4.49
          c 0 3.792 1.983 7.122 4.961 9.031 C 8.659 67.712 4.349 72.829 4.349 78.94 V
          90 h 31.632 V 78.94 C 35.981 72.829 31.67 67.712 25.932 66.453 z M 11.437
          57.422 v -4.49 c 0 -4.813 3.915 -8.728 8.728 -8.728 s 8.728 3.915 8.728 8.728
          v 4.49 c 0 4.813 -3.916 8.728 -8.728 8.728 S 11.437 62.234 11.437 57.422 z
          M 33.981 88 H 6.349 v -9.06 c 0 -5.95 4.84 -10.791 10.79 -10.791 h 6.052 c
          5.95 0 10.79 4.841 10.79 10.791 V 88 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: '#37CDA3',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) "
          strokeLinecap="round" />
        <path
          d="M 75.603 66.453 c 2.978 -1.908 4.961 -5.239 4.961 -9.031 v -4.49 c 0 -5.915
          -4.813 -10.728 -10.729 -10.728 c -5.915 0 -10.728 4.813 -10.728 10.728 v
          4.49 c 0 3.792 1.983 7.122 4.961 9.031 c -5.739 1.259 -10.05 6.377 -10.05
          12.488 V 90 h 31.633 V 78.94 C 85.651 72.829 81.341 67.712 75.603 66.453 z M
          61.107 57.422 v -4.49 c 0 -4.813 3.915 -8.728 8.728 -8.728 s 8.729 3.915 8.729
          8.728 v 4.49 c 0 4.813 -3.916 8.728 -8.729 8.728 S 61.107 62.234 61.107
          57.422 z M 83.651 88 H 56.019 v -9.06 c 0 -5.95 4.841 -10.791 10.791 -10.791
          h 6.052 c 5.949 0 10.79 4.841 10.79 10.791 V 88 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: '#37CDA3',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) "
          strokeLinecap="round" />
        <path
          d="M 68.061 37.97 c 0.186 0.163 0.421 0.248 0.658 0.248 c 0.14 0 0.281 -0.029
          0.413 -0.089 c 0.357 -0.162 0.587 -0.518 0.587 -0.911 v -7.429 h 3.849 c 4.914
          0 8.911 -3.998 8.911 -8.911 v -4.338 c 0 -4.914 -3.997 -8.911 -8.911 -8.911
          H 64.82 C 63.635 3.162 59.59 0 54.897 0 H 17.8 C 12.132 0 7.521 4.611 7.521
          10.279 v 5.088 c 0 5.668 4.611 10.279 10.279 10.279 h 4.687 v 8.886 c 0 0.393
          0.229 0.749 0.587 0.911 c 0.132 0.06 0.273 0.089 0.413 0.089 c 0.238 0 0.473
          -0.085 0.659 -0.248 l 10.552 -9.233 c 1.618 2.258 4.257 3.736 7.241 3.736 H
          58.71 L 68.061 37.97 z M 33.697 24.271 l -9.21 8.059 v -7.683 c 0 -0.552 -0.448
          -1 -1 -1 H 17.8 c -4.565 0 -8.279 -3.714 -8.279 -8.279 v -5.088 C 9.521 5.714
          13.235 2 17.8 2 h 37.097 c 3.597 0 6.723 2.305 7.839 5.628 H 41.939 c -4.914 0
          -8.911 3.997 -8.911 8.911 v 4.338 c 0 0.296 0.016 0.592 0.045 0.885 c 0.015 0.153
          0.044 0.302 0.067 0.452 c 0.021 0.139 0.036 0.279 0.064 0.417 c 0.03 0.15 0.073
          0.296 0.11 0.443 c 0.035 0.138 0.065 0.277 0.106 0.413 c 0.034 0.111 0.078 0.218
          0.116 0.327 C 33.589 23.967 33.636 24.122 33.697 24.271 z M 35.787 24.012 c -0.089
          -0.175 -0.174 -0.352 -0.248 -0.534 c -0.035 -0.085 -0.063 -0.172 -0.095 -0.258 c
          -0.06 -0.167 -0.115 -0.336 -0.162 -0.507 c -0.023 -0.084 -0.045 -0.169 -0.065
          -0.254 c -0.044 -0.186 -0.078 -0.375 -0.106 -0.564 c -0.011 -0.074 -0.025 -0.146
          -0.034 -0.22 c -0.031 -0.264 -0.05 -0.53 -0.05 -0.797 v -4.338 c 0 -3.811 3.1
          -6.911 6.911 -6.911 h 22.083 h 9.546 c 3.811 0 6.911 3.1 6.911 6.911 v 4.338 c 0
          3.811 -3.101 6.911 -6.911 6.911 h -4.849 c -0.553 0 -1 0.448 -1 1 v 6.225 l -7.975
          -6.978 c -0.182 -0.16 -0.416 -0.248 -0.658 -0.248 H 41.939 C 39.257 27.789 36.932
          26.25 35.787 24.012 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: '#37CDA3',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) "
          strokeLinecap="round" />
        <path
          d="M 65.721 20.03 h 6.034 c 0.553 0 1 -0.448 1 -1 s -0.447 -1 -1 -1 h -6.034 c
          -0.553 0 -1 0.448 -1 1 S 65.168 20.03 65.721 20.03 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: '#37CDA3', fillRule: 'nonzero',
            opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) "
          strokeLinecap="round" />
        <path
          d="M 58.075 18.03 h -13.68 c -0.552 0 -1 0.448 -1 1 s 0.448 1 1 1 h 13.68 c
          0.553 0 1 -0.448 1 -1 S 58.628 18.03 58.075 18.03 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: '#37CDA3',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) "
          strokeLinecap="round" />
      </g>
    </svg>
  );
};

const IconHome = props => {
  const { rootClassName, className } = props;
  const classes = classNames(rootClassName || css.homeIcon, className);
  return (
    <svg
      className={classes}
      xmlns="http://www.w3.org/2000/svg"
      width="56"
      height="56"
      fill="none"
      viewBox="0 0 1024 1024"
    >
      <path
        transform="scale(2 2)"
        d="M110.718 241.061C103.805 245.245 98.2898 252.416 90.1529 254.314C68.7295
        259.311 52.454 235.108 64.4366 216.53C67.4011 211.934 72.6473 208.959 76.9233
        205.73L228.276 90.5511C235.762 84.7812 243.39 76.8786 252.913 74.8373C262.741
        73.9265 265.857 76.714 273.058 82.3311L341.884 135.017L341.884 117.243C336.079
        117.159 331.844 114.08 331.242 108.065C330.846 104.1 330.936 88.0945 331.736
        84.617C334.32 73.3879 344.46 74.8181 353.252 74.8246L392.418 74.8192C412.162
        74.8104 412.292 78.8492 412.238 99.0649C412.212 108.412 413.601 117.123 401.536
        117.243L401.533 159.58C401.534 166.476 401.916 173.552 401.536 180.425L402.631
        181.231C412.5 188.617 440.676 208.953 446.539 216.427C457.772 230.743 448.789
        252.295 431.015 254.641C418.382 256.308 410.77 248.2 401.536 241.061L401.528
        403.533C401.53 409.711 402.034 416.233 401.457 422.366C400.723 430.158 383.09
        427.322 377.235 427.321L340.625 427.306L140.671 427.296C133.013 427.294 124.514
        427.777 116.956 427.253C107.494 426.597 110.71 411.406 110.712 404.38L110.718
        241.061ZM255.367 85.3965C248.375 87.0216 242.589 93.1222 237.049 97.3847L94.0811
        206.205C88.3057 210.534 82.3511 214.713 76.6791 219.174C62.3839 230.416 77.1054
        251.459 91.7472 242.297C94.1861 240.771 96.365 238.786 98.6646 237.059L241.579
        127.616C245.737 124.474 251.676 117.478 257.455 118.903C263.499 121.941 268.745
        126.761 274.114 130.827L411.949 236.051C417.843 240.558 423.733 246.619 431.845
        244.158C439.047 241.973 442.513 233.504 440.265 226.613C438.317 220.645 431.663
        216.909 426.943 213.279L409.793 200.033C397.604 190.469 385.429 180.847 373.056
        171.523L267.823 91.9116C264.493 89.3802 259.934 84.7499 255.367 85.3965ZM376.183
        85.3965C364.758 85.39 353.307 85.2278 341.884 85.3965L341.884 106.871L367.639
        106.871L401.536 106.871C401.547 99.7538 401.855 92.4961 401.536 85.3965C393.097
        85.2174 384.626 85.4085 376.183 85.3965ZM353.009 117.243L353.009 143.394C356.506
        146.126 389.504 171.413 391.046 171.449L391.046 117.243L353.009 117.243ZM255.367
        130.433C244.282 139.349 232.926 147.844 221.652 156.512L138.946 220.059C133.237
        224.486 127.552 229.381 121.529 233.341L121.529 416.708L211.763 416.708L211.751
        328.914C211.734 303.903 216.445 300.967 238.921 300.985L258.312 301.005L274.858
        300.986C295.696 300.978 300.643 303.069 300.578 326.851L300.548 416.708L381.28
        416.727C384.496 416.736 387.849 416.958 391.046 416.708L391.046 233.341L281.914
        150.438C278.13 147.5 258.166 131.367 255.367 130.433ZM233.883 311.289C219.486
        311.721 222.27 318.393 222.269 330.17L222.278 416.708L278.625 416.708L290.336
        416.708L290.349 328.246C290.369 307.308 286.247 311.259 267.771 311.27L233.883 311.289Z" />
      <path
        transform="scale(2 2)"
        d="M195.531 277.763L195.531 213.014L316.31 213.014L316.31 277.763L195.531
        277.763ZM206.205 223.466L206.205 267.289L250.669 267.289L250.66 237.937C250.658
        233.143 250.409 228.242 250.669 223.466L206.205 223.466ZM261.484 223.466L261.484
        267.289L305.773 267.289L305.773 223.466L261.484 223.466Z" />
      <path
        transform="scale(2 2)"
        d="M238.226 379.714C235.867 379.942 233.702 379.514 232.534 377.175C231.362
        374.829 228.722 352.446 235.773 349.546C245.26 349.04 242.415 368.827 242.303
        376.127C241.237 377.934 240.143 378.861 238.226 379.714Z" />
    </svg>
  );
};

const IconLocation2 = props => {
  const { rootClassName, className } = props;
  const classes = classNames(rootClassName || css.locationIcon2, className);
  return (
    <svg
      className={classes}
      xmlns="http://www.w3.org/2000/svg"
      width="40"
      height="40"
      fill="none"
      viewBox="0 0 256 256"
    >
      <g
        style={{
          stroke: 'none', strokeWidth: 0, strokeDasharray: 'none', strokeLinecap: 'butt',
          strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: 'none',
          fillRule: 'nonzero', opacity: 1
        }}
        transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)"
      >
        <path
          d="M 45 1.802 c 16.583 0 30.075 13.491 30.075 30.075 c 0 7.102 -2.538
          14.004 -7.145 19.434 L 45 78.317 L 22.07 51.311 c -4.608 -5.43 -7.145 -12.332
          -7.145 -19.434 C 14.925 15.294 28.417 1.802 45 1.802 M 45 45.692 c 8.222 0 14.912
          -6.689 14.912 -14.912 c 0 -8.222 -6.689 -14.912 -14.912 -14.912 S 30.088 22.557
          30.088 30.78 C 30.088 39.002 36.778 45.692 45 45.692 M 45 0 C 27.395 0 13.123
          14.272 13.123 31.877 c 0 7.86 2.858 15.043 7.573 20.6 L 45 81.101 l 24.304 -28.624
          c 4.716 -5.558 7.573 -12.741 7.573 -20.6 C 76.877 14.272 62.605 0 45 0 L 45 0 z M 45
          43.889 c -7.24 0 -13.11 -5.869 -13.11 -13.11 c 0 -7.24 5.869 -13.11 13.11 -13.11
          s 13.11 5.869 13.11 13.11 C 58.11 38.02 52.24 43.889 45 43.889 L 45 43.889 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: 'rgb(0,0,0)',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) "
          strokeLinecap="round" />
        <path
          d="M 58.814 71.531 l -1.575 1.575 c 9.772 1.532 15.583 4.707 15.583 7.094 c 0
          3.261 -10.838 7.997 -27.822 7.997 S 17.178 83.461 17.178 80.2 c 0 -2.387 5.811
          -5.562 15.583 -7.094 l -1.575 -1.575 c -9.401 1.643 -15.81 4.907 -15.81 8.669 c
          0 5.412 13.263 9.8 29.625 9.8 c 16.361 0 29.625 -4.388 29.625 -9.8 C 74.625
          76.439 68.215 73.174 58.814 71.531 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: 'rgb(0,0,0)',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) "
          strokeLinecap="round" />
      </g>
    </svg>
  );
};

const IconPlane = props => {
  const { rootClassName, className } = props;
  const classes = classNames(rootClassName || css.planeIcon, className);
  return (
    <svg
      className={classes}
      xmlns="http://www.w3.org/2000/svg"
      width="40"
      height="40"
      fill="none"
      viewBox="0 0 490 490"
    >
      <path
        d="M348.36,490L245,437.265L141.64,490v-64.682l51.467-55.236v-64.686L0,363.323v-63.507l193.107-137.082V60.343
	      C193.107,27.069,216.39,0,245,0c28.609,0,51.893,27.069,51.893,60.343v102.391L490,299.816v63.507l-193.107-57.927v64.686
	      l51.467,55.236V490z M245,414.104l82.509,42.099v-22.87l-51.467-55.236V277.645l193.107,57.927v-25.219L276.042,173.27V60.343
	      c0-21.93-13.927-39.773-31.042-39.773c-17.115,0-31.042,17.843-31.042,39.773V173.27L20.851,310.352v25.219l193.107-57.927v100.453
	      l-51.467,55.236v22.87L245,414.104z"
      />
    </svg>
  );
};

const IconCar = props => {
  const { rootClassName, className } = props;
  const classes = classNames(rootClassName || css.carIcon, className);
  return (
    <svg
      className={classes}
      xmlns="http://www.w3.org/2000/svg"
      width="40"
      height="40"
      fill="none"
      viewBox="0 0 64 64"
    >
      <g>
        <path d="M58.982,32.088C58.985,32.057,59,32.031,59,32c0-2.866-0.589-28-21-28H26C12.654,4,5,14.206,5,32
		c0,0.031,0.015,0.057,0.018,0.088C2.176,32.559,0,35.027,0,38v10c0,3.309,2.691,6,6,6v5c0,0.553,0.447,1,1,1h8c0.553,0,1-0.447,1-1
		v-5h32v5c0,0.553,0.447,1,1,1h8c0.553,0,1-0.447,1-1v-5c3.309,0,6-2.691,6-6V38C64,35.027,61.824,32.559,58.982,32.088z M26,6h12
		c18.467,0,19,23.339,19,26h-2c0-2.456-0.505-24-18-24H27C15.393,8,9,16.523,9,32H7C7,9.378,18.904,6,26,6z M24,24
		c-4.418,0-8,3.582-8,8h-5c0-18.184,8.701-22,16-22h10c6.34,0,10.909,3.16,13.581,9.394C52.825,24.632,53,30.367,53,32H32
		C32,27.582,28.418,24,24,24z M30,32H18c0-3.313,2.687-6,6-6S30,28.687,30,32z M14,58H8v-4h6V58z M56,58h-6v-4h6V58z M62,48
		c0,2.206-1.794,4-4,4H6c-2.206,0-4-1.794-4-4V38c0-2.206,1.794-4,4-4h52c2.206,0,4,1.794,4,4V48z" />
        <path d="M11,39c-2.206,0-4,1.794-4,4s1.794,4,4,4s4-1.794,4-4S13.206,39,11,39z M11,45c-1.103,0-2-0.897-2-2
		s0.897-2,2-2s2,0.897,2,2S12.103,45,11,45z" />
        <path d="M53,39c-2.206,0-4,1.794-4,4s1.794,4,4,4s4-1.794,4-4S55.206,39,53,39z M53,45c-1.103,0-2-0.897-2-2
		s0.897-2,2-2s2,0.897,2,2S54.103,45,53,45z" />
        <path
              d="M43,40H21c-0.553,0-1,0.447-1,1s0.447,1,1,1h22c0.553,0,1-0.447,1-1S43.553,40,43,40z" />
        <path
              d="M43,44H21c-0.553,0-1,0.447-1,1s0.447,1,1,1h22c0.553,0,1-0.447,1-1S43.553,44,43,44z" />
        <path d="M42.707,15.294c-0.391-0.391-1.023-0.391-1.414,0s-0.391,1.023,0,1.414l2,2
		c0.195,0.195,0.451,0.293,0.707,0.293s0.512-0.098,0.707-0.293c0.391-0.391,0.391-1.023,0-1.414L42.707,15.294z" />
        <path d="M37.707,15.293c-0.391-0.391-1.023-0.391-1.414,0s-0.391,1.023,0,1.414l7,7C43.488,23.902,43.744,24,44,24
		s0.512-0.098,0.707-0.293c0.391-0.391,0.391-1.023,0-1.414L37.707,15.293z" />
      </g>
    </svg>
  );
};

const IconBus = props => {
  const { rootClassName, className } = props;
  const classes = classNames(rootClassName || css.busIcon, className);
  return (
    <svg
      className={classes}
      xmlns="http://www.w3.org/2000/svg"
      width="40"
      height="40"
      fill="none"
      viewBox="0 0 256 256"
    >
      <g
        style={{
          stroke: 'none', strokeWidth: 0, strokeDasharray: 'none', strokeLinecap: 'butt',
          strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: 'none',
          fillRule: 'nonzero', opacity: 1
        }}
        transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)"
      >
        <path
          d="M 24.862 68.195 c -4.206 0 -7.628 -3.422 -7.628 -7.629 c 0 -4.206 3.422
          -7.628 7.628 -7.628 s 7.629 3.422 7.629 7.628 C 32.491 64.773 29.068 68.195
          24.862 68.195 z M 24.862 55.905 c -2.57 0 -4.661 2.091 -4.661 4.661 c 0 2.571
          2.091 4.662 4.661 4.662 c 2.571 0 4.662 -2.091 4.662 -4.662 C 29.524 57.996
          27.432 55.905 24.862 55.905 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: 'rgb(0,0,0)',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) "
          strokeLinecap="round"
        />
        <path
          d="M 65.138 68.195 c -4.206 0 -7.628 -3.422 -7.628 -7.629 c 0 -4.206 3.422
          -7.628 7.628 -7.628 c 4.207 0 7.629 3.422 7.629 7.628 C 72.767 64.773 69.345
          68.195 65.138 68.195 z M 65.138 55.905 c -2.57 0 -4.661 2.091 -4.661 4.661 c
          0 2.571 2.091 4.662 4.661 4.662 c 2.571 0 4.662 -2.091 4.662 -4.662 C 69.8
          57.996 67.709 55.905 65.138 55.905 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: 'rgb(0,0,0)',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) "
          strokeLinecap="round"
        />
        <path
          d="M 86.997 9.997 H 79.89 h -0.071 v -4.22 C 79.818 2.592 77.226 0 74.041 0 H
          15.959 c -3.186 0 -5.778 2.592 -5.778 5.778 v 4.22 H 10.11 H 3.003 c -0.82 0
          -1.484 0.664 -1.484 1.484 v 15.103 c 0 0.82 0.664 1.484 1.484 1.484 s 1.484
          -0.664 1.484 -1.484 v -13.62 h 5.624 h 0.071 v 58.128 c 0 3.185 2.592 5.777
          5.778 5.777 h 0.414 v 8.098 c 0 2.776 2.258 5.034 5.033 5.034 h 6.912 c 2.775
          0 5.033 -2.258 5.033 -5.034 v -8.098 h 23.296 v 8.098 c 0 2.776 2.258 5.034
          5.034 5.034 h 6.912 c 2.775 0 5.033 -2.258 5.033 -5.034 v -8.098 h 0.413 c
          3.185 0 5.778 -2.591 5.778 -5.777 V 12.964 h 0.071 h 5.624 v 13.62 c 0 0.82
          0.664 1.484 1.484 1.484 s 1.484 -0.664 1.484 -1.484 V 11.481 C 88.481 10.661
          87.816 9.997 86.997 9.997 z M 13.148 5.778 c 0 -1.55 1.261 -2.811 2.811 -2.811
          h 58.082 c 1.55 0 2.811 1.261 2.811 2.811 v 4.22 H 13.148 V 5.778 z M 30.385
          84.966 c 0 1.14 -0.927 2.067 -2.066 2.067 h -6.912 c -1.139 0 -2.066 -0.927 -2.066
          -2.067 v -8.098 h 11.045 V 84.966 z M 70.66 84.966 c 0 1.14 -0.927 2.067 -2.066
          2.067 h -6.912 c -1.14 0 -2.067 -0.927 -2.067 -2.067 v -8.098 H 70.66 V 84.966
          z M 74.041 73.902 H 15.959 c -1.55 0 -2.811 -1.26 -2.811 -2.81 v -24.96 h 63.703
          v 24.96 C 76.851 72.641 75.591 73.902 74.041 73.902 z M 76.851 43.165 H 13.148
          V 12.964 h 63.703 V 43.165 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: 'rgb(0,0,0)',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) "
          strokeLinecap="round"
        />
        <path
          d="M 50.21 62.05 H 39.79 c -0.82 0 -1.484 -0.664 -1.484 -1.484 c 0 -0.819
          0.664 -1.484 1.484 -1.484 h 10.42 c 0.819 0 1.484 0.664 1.484 1.484 C 51.693
          61.385 51.029 62.05 50.21 62.05 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: 'rgb(0,0,0)',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) "
          strokeLinecap="round"
        />
      </g>
    </svg>
  );
};

const IconBook = props => {
  const { rootClassName, className } = props;
  const classes = classNames(rootClassName || css.bookIcon, className);
  return (
    <svg
      className={classes}
      xmlns="http://www.w3.org/2000/svg"
      width="40"
      height="40"
      fill="none"
      viewBox="0 0 256 256"
    >
      <g
        style={{
          stroke: 'none', strokeWidth: 0, strokeDasharray: 'none', strokeLinecap: 'butt',
          strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: 'none',
          fillRule: 'nonzero', opacity: 1
        }}
        transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)">
        <path
          d="M 89 30.872 h -5.723 V 16.685 c 0 -0.271 -0.11 -0.531 -0.305 -0.719 c -0.196
          -0.188 -0.452 -0.294 -0.729 -0.28 c -4.328 0.149 -8.538 0.752 -12.554 1.749 V
          6.489 c 0 -0.344 -0.177 -0.665 -0.469 -0.847 c -0.293 -0.184 -0.656 -0.203
          -0.968 -0.053 c -8.89 4.309 -16.793 12.648 -23.51 24.775 C 34.734 21.111 22.995
          16.165 9.821 15.685 c -0.279 -0.019 -0.535 0.091 -0.731 0.28 c -0.195 0.188
          -0.306 0.448 -0.306 0.72 v 7.33 c -1.392 0.098 -2.803 0.225 -4.22 0.4 c -0.504
          0.063 -0.882 0.493 -0.877 1.001 l 0.047 5.456 H 0 v 53.639 h 45 h 44 c 0.553 0 1
          -0.447 1 -1 V 31.872 C 90 31.32 89.553 30.872 89 30.872 z M 81.277 17.728 v 49.546
          c -12.104 0.599 -23.047 4.597 -32.62 11.893 c 6.014 -9.756 12.883 -16.554 20.469
          -20.23 c 0.345 -0.167 0.563 -0.517 0.563 -0.899 v -38.55 C 73.393 18.533 77.278
          17.932 81.277 17.728 z M 67.689 8.127 v 49.291 C 59.575 61.524 52.293 69.006 46
          79.693 V 32.215 C 52.254 20.646 59.544 12.552 67.689 8.127 z M 44 32.39 v 48.732
          c -0.102 -0.058 -0.206 -0.112 -0.309 -0.17 c -9.529 -8.42 -20.581 -13.019 -32.907
          -13.675 V 24.922 c 0 -0.013 0.006 -0.024 0.005 -0.038 c 0 -0.007 -0.005 -0.013
          -0.005 -0.02 v -7.133 C 23.312 18.424 34.48 23.352 44 32.39 z M 8.784 26.017 v
          42.216 c 0 0.538 0.426 0.979 0.963 0.999 c 9.224 0.336 17.72 2.954 25.382 7.782 c
          -8.824 -3.185 -18.537 -4.216 -29.023 -3.063 L 5.694 26.296 C 6.732 26.179 7.763
          26.091 8.784 26.017 z M 2 32.872 h 1.751 l 0.366 42.211 c 0.002 0.285 0.126 0.556
          0.341 0.743 c 0.214 0.188 0.501 0.273 0.782 0.24 c 13.865 -1.716 26.332 0.461 37.135
          6.444 H 2 V 32.872 z M 88 82.511 H 47.668 c 10 -8.366 21.642 -12.831 34.643 -13.278 c
          0.539 -0.019 0.966 -0.46 0.966 -0.999 V 32.872 H 88 V 82.511 z"
          style={{
            stroke: 'none', strokeWidth: 1, strokeDasharray: 'none', strokeLinecap: 'butt',
            strokeLinejoin: 'miter', strokeMiterlimit: 10, fill: 'rgb(0,0,0)',
            fillRule: 'nonzero', opacity: 1
          }}
          transform=" matrix(1 0 0 1 0 0) "
          strokeLinecap="round"
        />
      </g>
    </svg>
  );
};

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id,
  );
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews = [],
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    ...restOfProps
  } = props;

  const derivedData = getDerivedRenderData({
    rawParams,
    getListing,
    getOwnListing,
    showOwnListingsOnly,
    currentUser,
    config,
    intl,
    location,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
    longWordClassName: css.longWord,
    payoutDetailsWarningClassName: css.payoutDetailsWarning,
  });
  const {
    listingConfig,
    listingId,
    isVariant,
    currentListing,
    listingSlug,
    params,
    listingPathParamType,
    listingTab,
    description,
    geolocation,
    price,
    title,
    publicData,
    metadata,
    richTitle,
    isOwnListing,
    showListingImage,
    showDescription,
    processType,
    ensuredAuthor,
    noPayoutDetailsSetWithOwnListing,
    payoutDetailsWarning,
    authorDisplayName,
    schemaTitle,
    facebookImages,
    twitterImages,
    schemaImages,
    productURL,
    availabilityMaybe,
    noIndexMaybe,
    hasInvalidListingData,
  } = derivedData;

  const { listingType, isResidency } = currentListing?.attributes?.publicData || {};
  const { createdAt: authorSignedUpAt } = currentListing?.author?.attributes || {};

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  if (hasInvalidListingData) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }
  const unitType = publicData.unitType;
  const isNegotiation = processType === 'negotiation';

  const commonParams = { params, history, routes: routeConfiguration, intl };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    setInitialValues, // from ListingPage.duck.js (set initial values for the listing page)
    location,
    setInquiryModalOpen,
  });
  // Note: this is for inquire transition to inquiry state in booking, purchase and negotiation processes.
  // Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else if (isNegotiation && unitType === REQUEST) {
      // This is to navigate to MakeOfferPage when InvokeNegotiationForm is submitted
      const onNavigateToMakeOfferPage = handleNavigateToMakeOfferPage({
        ...commonParams,
        getListing,
      });
      onNavigateToMakeOfferPage(values);
    } else if (isNegotiation && unitType === OFFER) {
      // This is to navigate to MakeOfferPage when InvokeNegotiationForm is submitted
      const onNavigateToRequestQuotePage = handleNavigateToRequestQuotePage({
        ...commonParams,
        getListing,
      });
      onNavigateToRequestQuotePage(values);
    } else {
      const onSubmit = handleSubmit({
        ...commonParams,
        currentUser,
        callSetInitialValues,
        getListing,
        onInitializeCardPaymentData,
      });
      onSubmit(values);
    }
  };

  const listingTypeBadge = listingType === 'curated_programmes' ? (
    <div className={css.listingTypeBadge}>
      <FormattedMessage id="ListingPage.curatedProgrammeTypeLabel" />
    </div>
  ) : (
    <div className={css.listingTypeBadge}>
      <FormattedMessage id="ListingPage.creativeStayTypeLabel" />
    </div>
  );

  const residencyBadge = isResidency ? (
    <div className={css.residencyBadge}>
      <FormattedMessage id="ListingPage.residencyMarkLabel" />
    </div>
  ) : null;

  const earlyHostBadge = authorSignedUpAt && authorSignedUpAt < RELEASE_DATE_TIME ? (
    <div className={css.earlyHostBadge}>
      <FormattedMessage id="ListingPage.earlyHostMarkLabel" />
    </div>
  ) : null;

  const badgesSection = (
    <div className={css.badgeSection}>
      {listingTypeBadge}
      {residencyBadge}
      {earlyHostBadge}
    </div>
  );

  const whyBookSection = (
    <div className={css.whyBookSection}>
      <div className={css.whyBookTitle}>
        <FormattedMessage id="ListingPage.whyBookTitle" />
      </div>
      <div className={css.whyBookRow}>
        <IconShield />
        <div className={css.whyBookText}>
          <b><span><FormattedMessage id="ListingPage.secureBookingTitle" /></span></b>
          <span><FormattedMessage id="ListingPage.secureBookingText" /></span>
        </div>
      </div>
      <div className={css.whyBookRow}>
        <IconSupport />
        <div className={css.whyBookText}>
          <b><span><FormattedMessage id="ListingPage.supportTeamTitle" /></span></b>
          <span><FormattedMessage id="ListingPage.supportTeamText" /></span>
        </div>
      </div>
      <div className={css.whyBookRow}>
        <IconCancel />
        <div className={css.whyBookText}>
          <b><span><FormattedMessage id="ListingPage.flexibleCancellationTitle" /></span></b>
          <span><FormattedMessage id="ListingPage.flexibleCancellationText" /></span>
        </div>
      </div>
      <div className={css.whyBookRow}>
        <IconTrust />
        <div className={css.whyBookText}>
          <b><span><FormattedMessage id="ListingPage.creativesTrustTitle" /></span></b>
          <span><FormattedMessage id="ListingPage.creativesTrustText" /></span>
        </div>
      </div>
    </div>
  );

  const listing = getListing(listingId);
  const aboutHostBio = listing?.author?.attributes?.profile?.bio
    ? listing?.author?.attributes?.profile?.bio.split('.')[ 0 ] + '.' : null;
  const aboutHostSection = (
    <div className={css.aboutHostSection}>
      <div className={css.aboutHostTitle}>
        <FormattedMessage id="ListingPage.aboutHostTitle" />
      </div>
      <div className={css.aboutHostBlock}>
        <div className={css.hostAvatarWrapper}>
          <AvatarLarge user={listing.author} className={css.hostAvatar} />
        </div>
        <div className={css.aboutHostContent}>
          <div className={css.aboutHostVerified}>
            <FormattedMessage id="ListingPage.verifiedHostTitle" />
            <IconVerified />
          </div>
          <div className={css.aboutHostBio}>
            {aboutHostBio}
          </div>
        </div>
      </div>
      <div className={css.learnMoreBlock}>
        <NamedLink name="ProfilePage" params={{ id: listing?.author?.id?.uuid }}>
          <FormattedMessage id="ListingPage.learnMoreLink" />
        </NamedLink>
      </div>
    </div>
  );

  const excludedFields = [
    'accommodation_type', 'bathroom_type', 'brief_description', 'creative_community',
    'studio_access', 'why_love_reasons', 'nearest_village', 'nearest_airport',
    'car_recommended', 'local_transport', 'explore_guidebook'
  ];
  const fieldsConfig = listingConfig.listingFields.filter( field =>
    !excludedFields.includes( field.key )
  );

  const briefDescription = (
    <div className={css.briefDescription}>
      {listing?.attributes?.publicData?.brief_description}
    </div>
  );

  const locationSection = (
    <div className={css.locationSection}>
      <IconLocation />
      {listing?.attributes?.publicData?.location?.address}
    </div>
  );

  const amenities = [];

  const accomodationType = listing?.attributes?.publicData?.accommodation_type;
  const accomodationTypes = {
    private_room: {
      title: <FormattedMessage id="ListingPage.accommodationTypeTitleOwnRoom" />,
      description: <FormattedMessage id="ListingPage.accommodationTypeDescriptionOwnRoom" />

    },
    shared_room: {
      title: <FormattedMessage id="ListingPage.accommodationTypeTitleSharedRoom" />,
      description: <FormattedMessage id="ListingPage.accommodationTypeDescriptionSharedRoom" />
    },
    entire_place: {
      title: <FormattedMessage id="ListingPage.accommodationTypeTitleEntirePlace" />,
      description: <FormattedMessage id="ListingPage.accommodationTypeDescriptionEntirePlace" />
    }
  };

  amenities.push(
    <>
      <IconDoubleBed />
      <div className={css.amenityText}>
        <div className={css.amenityTitle}>
          {accomodationType ? accomodationTypes[ accomodationType ].title : null}
        </div>
        <div className={css.amenityDescription}>
          {accomodationType ? accomodationTypes[ accomodationType ].description : null}
        </div>
      </div>
    </>
  );

  const studioAccessType = listing?.attributes?.publicData?.studio_access || 'none';
  const studioAccessTypes = {
    dedicated: {
      title: <FormattedMessage id="ListingPage.studioAccessTitleDedicated" />,
      description: <FormattedMessage id="ListingPage.studioAccessDescriptionDedicated" />

    },
    shared: {
      title: <FormattedMessage id="ListingPage.studioAccessTitleShared" />,
      description: <FormattedMessage id="ListingPage.studioAccessDescriptionShared" />
    },
    none: {
      title: <FormattedMessage id="ListingPage.studioAccessTitleNone" />,
      description: <FormattedMessage id="ListingPage.studioAccessDescriptionNone" />
    }
  };

  amenities.push(
    <>
      <IconWhiteboard />
      <div className={css.amenityText}>
        <div className={css.amenityTitle}>
          {studioAccessType ? studioAccessTypes[ studioAccessType ]?.title : null}
        </div>
        <div className={css.amenityDescription}>
          {studioAccessType ? studioAccessTypes[ studioAccessType ]?.description : null}
        </div>
      </div>
    </>
  );

  if( listing?.attributes?.publicData?.creative_community ){
    amenities.push(
      <>
        <IconCommunity />
        <div className={css.amenityText}>
          <div className={css.amenityTitle}>
            <FormattedMessage id="ListingPage.creativeCommunityTitle" />
          </div>
          <div className={css.amenityDescription}>
            {listing?.attributes?.publicData?.creative_community}
          </div>
        </div>
      </>
    );
  }

  const bathroomTypes = {
    personal: {
      title: <FormattedMessage id="ListingPage.bathroomTypeTitlePersonal" />,
      description: <FormattedMessage id="ListingPage.bathroomTypeDescriptionPersonal" />
    },
    shared: {
      title: <FormattedMessage id="ListingPage.bathroomTypeTitleShared" />,
      description: <FormattedMessage id="ListingPage.bathroomTypeDescriptionShared" />
    },
  };

  if( listing?.attributes?.publicData?.bathroom_type ){
    amenities.push(
      <>
        <IconShower />
        <div className={css.amenityText}>
          <div className={css.amenityTitle}>
            {bathroomTypes[ listing?.attributes?.publicData?.bathroom_type ].title}
          </div>
          <div className={css.amenityDescription}>
            {bathroomTypes[ listing?.attributes?.publicData?.bathroom_type ].description}
          </div>
        </div>
      </>
    );
  }

  const minStayNights = listing?.attributes?.publicData?.minimumNights || 1;
  amenities.push(
    <>
      <IconCalendar />
      <div className={css.amenityText}>
        <div className={css.amenityTitle}>
          <FormattedMessage id="ListingPage.minStayNightsTitle" values={{ nights: minStayNights }} />
        </div>
        <div className={css.amenityDescription}>
          <FormattedMessage id="ListingPage.minStayNightsDescription" values={{ nights: minStayNights }} />
        </div>
      </div>
    </>
  );

  const amenityBlocks = amenities.map((amenity, index ) => (
    <div className={ index < amenities.length - 1 ? css.amenitiesBlock : css.amenitiesLastBlock }>
      {amenity}
    </div>
  ));

  const amenitiesSection = (
    <div className={css.amenitiesSection}>
      {amenityBlocks}
    </div>
  );

  const whyLoveReasons = listing?.attributes?.publicData?.why_love_reasons || [];
  const whyLoveFields = [];

  if( whyLoveReasons.length > 0 ){
    if( whyLoveReasons.includes("peaceful_setting")) {
      whyLoveFields.push({
        icon: <IconThreeLeaves />,
        title: <FormattedMessage id="ListingPage.whyLoveTitlePeacefulSetting" />,
        description: <FormattedMessage id="ListingPage.whyLoveDescriptionPeacefulSetting" />,
      })
    }

    if( whyLoveReasons.includes("inspiring_spaces")) {
      whyLoveFields.push({
        icon: <IconWhiteboard2 />,
        title: <FormattedMessage id="ListingPage.whyLoveTitleInspiringSpaces" />,
        description: <FormattedMessage id="ListingPage.whyLoveDescriptionInspiringSpaces" />,
      })
    }

    if( whyLoveReasons.includes("creative_community")) {
      whyLoveFields.push({
        icon: <IconCommunity2 />,
        title: <FormattedMessage id="ListingPage.whyLoveTitleCreativeCommunity" />,
        description: <FormattedMessage id="ListingPage.whyLoveDescriptionCreativeCommunity" />,
      })
    }

    if( whyLoveReasons.includes("local_culture")) {
      whyLoveFields.push({
        icon: <IconHome />,
        title: <FormattedMessage id="ListingPage.whyLoveTitleLocalCulture" />,
        description: <FormattedMessage id="ListingPage.whyLoveDescriptionLocalCulture" />,
      })
    }
  }

  const whyLoveBlocks = whyLoveFields.map((field, index ) => (
    <div className={index < whyLoveFields.length - 1 ? css.whyLoveBlock : css.whyLoveLastBlock}>
      {field.icon}
      <div className={css.whyLoveText}>
        <div className={css.whyLoveTitle}>{field.title}</div>
        <div className={css.whyLoveDescription}>{field.description}</div>
      </div>
    </div>
  ));

  const whyLoveSection = whyLoveFields.length > 0 ? (
    <div className={css.whyLoveSectionWrapper}>
      <span className={css.whyLoveSectionTitle}>
        <FormattedMessage id="ListingPage.whyLoveSectionTitle" />
      </span>
      <div className={css.whyLoveSection}>
        {whyLoveBlocks}
      </div>
    </div>
  ) : null;

  const extraAmenities = [
    {
      fieldId: 'nearest_village',
      icon: <IconLocation2 />,
      title: <FormattedMessage id="ListingPage.extraAmenityTitleNearestVillage" />
    },
    {
      fieldId: 'nearest_airport',
      icon: <IconPlane />,
      title: <FormattedMessage id="ListingPage.extraAmenityTitleNearestAirport" />
    },
    {
      fieldId: 'car_recommended',
      icon: <IconCar />,
      title: <FormattedMessage id="ListingPage.extraAmenityTitleCarRecommended" />
    },
    {
      fieldId: 'local_transport',
      icon: <IconBus />,
      title: <FormattedMessage id="ListingPage.extraAmenityTitleLocalTransport" />
    },
    {
      fieldId: 'explore_guidebook',
      icon: <IconBook />,
      title: <FormattedMessage id="ListingPage.extraAmenityTitleExploreGuidebook" />
    }
  ];

  const extraAmenityBlocks = [];
  const listingPublicData = listing?.attributes?.publicData || {};

  extraAmenities.forEach( amenity => {
    if (listingPublicData[ amenity.fieldId ]) {
      extraAmenityBlocks.push(
        <>
          {amenity.icon}
          <div className={css.extraAmenityText}>
            <div className={css.extraAmenityTitle}>
              {amenity.title}
            </div>
            <div className={css.extraAmenityDescription}>
              {listingPublicData[ amenity.fieldId ]}
            </div>
          </div>
        </>
      )
    }
  });

  const extraAmenityClauses = extraAmenityBlocks.map((amenity, index ) => (
    <div className={ index < extraAmenityBlocks.length - 1 ? css.extraAmenitiesBlock : css.extraAmenitiesLastBlock }>
      {amenity}
    </div>
  ));

  const extraAmenitiesSection = extraAmenityClauses.length > 0 ? (
    <div className={css.extraAmenitiesSection}>
      {extraAmenityClauses}
    </div>
  ) : null;

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      {...noIndexMaybe}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...priceForSchemaMaybe(price),
          ...availabilityMaybe,
        },
      }}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        <div className={css.pageWrapperForProductLayout}>
          <div className={css.contentWrapperForProductLayout}>
            <div className={css.mainColumnForProductLayout}>
              <NamedLink className={css.backToSearchLink} name="SearchPage" params={{}}>
                <FormattedMessage id="ListingPage.backToSearchLink"/>
              </NamedLink>
              <Notifications
                mounted={mounted}
                listing={currentListing}
                isOwnListing={isOwnListing}
                noPayoutDetailsSetWithOwnListing={noPayoutDetailsSetWithOwnListing}
                currentUser={currentUser}
                className={css.actionBarForProductLayout}
                editParams={{
                  id: listingId.uuid,
                  slug: listingSlug,
                  type: listingPathParamType,
                  tab: listingTab,
                }}
              />
              {showListingImage && (
                <SectionGallery
                  listing={currentListing}
                  variantPrefix={config.layout.listingImage.variantPrefix}
                />
              )}

              <H2 className={css.listingTitle}>{title}</H2>

              {badgesSection}
              {locationSection}

              <SectionReviews reviews={reviews} fetchReviewsError={fetchReviewsError} />

              {amenitiesSection}
              {briefDescription}
              {whyLoveSection}
            </div>
            <div className={css.orderColumnForProductLayout}>
              <OrderPanel
                className={classNames(css.productOrderPanel, {
                  [css.imagesEnabled]: showListingImage,
                })}
                listing={currentListing}
                isOwnListing={isOwnListing}
                onSubmit={handleOrderSubmit}
                authorLink={
                  <NamedLink
                    className={css.authorNameLink}
                    name={isVariant ? 'ListingPageVariant' : 'ListingPage'}
                    params={params}
                    to={{ hash: '#author' }}
                  >
                    {authorDisplayName}
                  </NamedLink>
                }
                title={<FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />}
                titleDesktop={
                  <H4 as="h1" className={css.orderPanelTitle}>
                    <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                  </H4>
                }
                payoutDetailsWarning={payoutDetailsWarning}
                author={ensuredAuthor}
                onManageDisableScrolling={onManageDisableScrolling}
                onContactUser={onContactUser}
                {...restOfProps}
                validListingTypes={config.listing.listingTypes}
                marketplaceCurrency={config.currency}
                dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
                marketplaceName={config.marketplaceName}
                showListingImage={showListingImage}
              />
              {whyBookSection}
              {aboutHostSection}
            </div>
          </div>
          <div className={css.extraContentForProductLayout}>
            {extraAmenitiesSection}

            {showDescription && (
              <>
                <div className={css.descriptionTitle}>
                  <FormattedMessage id="ListingPage.descriptionTitle" />
                </div>
                <SectionText text={description} showAsIngress />
              </>
            )}

            <CustomListingFields
              publicData={publicData}
              metadata={metadata}
              listingFieldConfigs={fieldsConfig}
              categoryConfiguration={config.categoryConfiguration}
              intl={intl}
            />

            <SectionMapMaybe
              geolocation={geolocation}
              publicData={publicData}
              listingId={currentListing.id}
              mapsConfig={config.maps}
            />

          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

/**
 * The ListingPage component with carousel layout.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.params - The path params object
 * @param {string} props.params.id - The listing id
 * @param {string} props.params.slug - The listing slug
 * @param {LISTING_PAGE_DRAFT_VARIANT | LISTING_PAGE_PENDING_APPROVAL_VARIANT} props.params.variant - The listing variant
 * @param {Function} props.onManageDisableScrolling - The on manage disable scrolling function
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {Function} props.getListing - The get listing function
 * @param {Function} props.getOwnListing - The get own listing function
 * @param {Object} props.currentUser - The current user
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @param {string} props.inquiryModalOpenForListingId - The inquiry modal open for the specific listing id
 * @param {propTypes.error} props.showListingError - The show listing error
 * @param {Function} props.callSetInitialValues - The call page-specific setInitialValues function, which is given to this function as a parameter
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.fetchReviewsError - The fetch reviews error
 * @param {Object<string, Object>} props.monthlyTimeSlots - The monthly time slots. E.g. { '2019-11': { timeSlots: [], fetchTimeSlotsInProgress: false, fetchTimeSlotsError: null } }
 * @param {Object<string, Object>} props.timeSlotsForDate - The time slots for date. E.g. { '2019-11-01': { timeSlots: [], fetchedAt: 1572566400000, fetchTimeSlotsError: null, fetchTimeSlotsInProgress: false } }
 * @param {boolean} props.sendInquiryInProgress - Whether the send inquiry is in progress
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @param {Function} props.onSendInquiry - The on send inquiry function
 * @param {Function} props.onInitializeCardPaymentData - The on initialize card payment data function
 * @param {Function} props.onFetchTimeSlots - The on fetch time slots function
 * @param {Function} props.onFetchTransactionLineItems - The on fetch transaction line items function
 * @param {Array<propTypes.transactionLineItem>} props.lineItems - The line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether the fetch line items is in progress
 * @param {propTypes.error} props.fetchLineItemsError - The fetch line items error
 * @returns {JSX.Element} listing page component
 */
const ListingPage = props => {
  const dispatch = useDispatch();
  const store = useStore();

  const { isAuthenticated } = useSelector(state => state.auth);
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
  } = useSelector(state => state.ListingPage);
  const currentUser = useSelector(state => state.user?.currentUser);
  const scrollingDisabled = useSelector(state => isScrollingDisabled(state));

  const getListing = useCallback(
    id => {
      const state = store.getState();
      const ref = { id, type: 'listing' };
      const listings = getMarketplaceEntities(state, [ref]);
      return listings.length === 1 ? listings[0] : null;
    },
    [store]
  );
  const getOwnListing = useCallback(
    id => {
      const state = store.getState();
      const ref = { id, type: 'ownListing' };
      const listings = getMarketplaceEntities(state, [ref]);
      return listings.length === 1 ? listings[0] : null;
    },
    [store]
  );

  const onManageDisableScrolling = useCallback(
    (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    [dispatch]
  );
  const callSetInitialValues = useCallback(
    (setInitialValuesFn, values, saveToSessionStorage) =>
      dispatch(setInitialValuesFn(values, saveToSessionStorage)),
    [dispatch]
  );
  const onFetchTransactionLineItems = useCallback(
    params => dispatch(fetchTransactionLineItems(params)),
    [dispatch]
  );
  const onSendInquiry = useCallback((listing, message) => dispatch(sendInquiry(listing, message)), [
    dispatch,
  ]);
  const onInitializeCardPaymentData = useCallback(() => dispatch(initializeCardPaymentData()), [
    dispatch,
  ]);
  const onFetchTimeSlots = useCallback(
    (listingId, start, end, timeZone, options) =>
      dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)),
    [dispatch]
  );

  return (
    <ListingPageAccessWrapper
      {...props}
      PageComponent={ListingPageComponent}
      isAuthenticated={isAuthenticated}
      currentUser={currentUser}
      getListing={getListing}
      getOwnListing={getOwnListing}
      scrollingDisabled={scrollingDisabled}
      inquiryModalOpenForListingId={inquiryModalOpenForListingId}
      showListingError={showListingError}
      reviews={reviews}
      fetchReviewsError={fetchReviewsError}
      monthlyTimeSlots={monthlyTimeSlots}
      timeSlotsForDate={timeSlotsForDate}
      lineItems={lineItems}
      fetchLineItemsInProgress={fetchLineItemsInProgress}
      fetchLineItemsError={fetchLineItemsError}
      sendInquiryInProgress={sendInquiryInProgress}
      sendInquiryError={sendInquiryError}
      onManageDisableScrolling={onManageDisableScrolling}
      callSetInitialValues={callSetInitialValues}
      onFetchTransactionLineItems={onFetchTransactionLineItems}
      onSendInquiry={onSendInquiry}
      onInitializeCardPaymentData={onInitializeCardPaymentData}
      onFetchTimeSlots={onFetchTimeSlots}
    />
  );
};

export default ListingPage;
